import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Plus, Zap, GitBranch, ChevronRight, Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/shared/PageHeader";
import StatCard from "@/components/shared/StatCard";
import SectionHeader from "@/components/shared/SectionHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { WORKFLOW_NODES, WORKFLOW_EDGES } from "@/data/mockData";
import Startworlflow from "./lead-engine/Startworlflow";
import { listCompanies, getCompany } from "./lead-engine/api";
import { StatusStepper } from "./lead-engine/StatusStepper";
import { CompanyResultView } from "./lead-engine/CompanyResultView";
import { formatDuration, durationBetween } from "./lead-engine/utils";

const HISTORY_PAGE_SIZE = 5;

export default function LeadEngine() {
  const [running, setRunning] = useState(true);

  const [companies, setCompanies] = useState([]);
  const [companiesTotal, setCompaniesTotal] = useState(0);
  const [companiesOffset, setCompaniesOffset] = useState(0);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  // Detail dialog - opened by clicking a history row.
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  // The id of the row the user most recently clicked. If they click a second
  // row before the first's getCompany() resolves, the first response arriving
  // late must not overwrite the second row's data - checked against this ref
  // (not state, so it's readable synchronously inside the async callback).
  const requestedIdRef = useRef(null);

  const openCompanyDetail = async (company) => {
    requestedIdRef.current = company.id;
    setDetailOpen(true);
    setSelectedCompany(company); // show what we already have immediately
    setLoadingDetail(true);
    try {
      const full = await getCompany(company.id);
      if (requestedIdRef.current === company.id) setSelectedCompany(full);
    } catch (err) {
      console.error("Failed to load company detail for", company.id, err);
      if (requestedIdRef.current === company.id) {
        toast.error("Could not load full details", {
          description: "Showing what's in the list for now — try reopening this company.",
        });
      }
    } finally {
      if (requestedIdRef.current === company.id) setLoadingDetail(false);
    }
  };

  const fetchCompanies = async (nextOffset = 0) => {
    setLoadingCompanies(true);
    try {
      const data = await listCompanies({ limit: HISTORY_PAGE_SIZE, offset: nextOffset });
      setCompanies(data.items);
      setCompaniesTotal(data.total_count);
      setCompaniesOffset(nextOffset);
    } catch (err) {
      console.error("Failed to load lead engine history", err);
    } finally {
      setLoadingCompanies(false);
    }
  };

  useEffect(() => {
    fetchCompanies(0);
  }, []);

  return (
    <div data-testid="leads-page">
      <PageHeader
        eyebrow="Module 02 · AI Lead Generation & Outreach Engine"
        title="Watch every lead move through the pipeline — live."
        description="An n8n-native workflow discovers companies, enriches them, personalises the pitch and books meetings. Every step is visible, editable and observable."
        actions={
          <>
            <Button
              data-testid="leads-toggle-run"
              onClick={() => {
                setRunning((v) => !v);
                toast.success(running ? "Workflow paused" : "Workflow running");
              }}
              className={`rounded-full ${running ? "bg-white/[0.05] border border-white/[0.08]" : "bg-emerald-500 hover:bg-emerald-400"}`}
            >
              {running ? <Pause className="h-3.5 w-3.5 mr-1.5" /> : <Play className="h-3.5 w-3.5 mr-1.5" />}
              {running ? "Pause workflow" : "Resume workflow"}
            </Button>
            <Button data-testid="leads-new-node" className="rounded-full bg-indigo-500 hover:bg-indigo-400">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add node
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <StatCard testId="leads-stat-flows" label="Active Flows" value="7" delta="+2" accent="indigo" icon={GitBranch} />
        <StatCard testId="leads-stat-emails" label="Emails Sent · 24h" value="1,048" delta="+312" accent="cyan" icon={Mail} />
        <StatCard testId="leads-stat-replies" label="Reply Rate" value="14.2" suffix="%" delta="+2.4pt" accent="emerald" />
        <StatCard testId="leads-stat-meetings" label="Meetings Booked" value="42" delta="+9" accent="amber" />
        <StatCard testId="leads-stat-cost" label="Cost per Meeting" value="18" prefix="$" delta="-24%" accent="pink" trend="up" />
      </div>

      {/* Workflow canvas */}
      <SectionHeader
        kicker="n8n · live workflow"
        title="Discovery → Meeting"
        action={
          <div className="flex items-center gap-2 text-[11px] font-mono text-white/50">
            <span className={`h-1.5 w-1.5 rounded-full ${running ? "bg-emerald-400 shadow-[0_0_8px_#10b981]" : "bg-white/30"}`} />
            {running ? "RUNNING" : "PAUSED"}
          </div>
        }
      />
      {/* <div className="glass-card p-0 overflow-hidden">
        <WorkflowCanvas running={running} />
      </div> */}

      {/* onRunFinished: History below is its own separate fetch (on mount
          only) - without this, a company that finishes processing while
          the user is already on this page wouldn't show up until a manual
          page reload. */}
      <Startworlflow onRunFinished={() => fetchCompanies(0)} />
      {/* Active outreach — real run history from the Lead Engine backend, not mock campaigns */}
      <SectionHeader
        className="mt-10"
        kicker="Lead Engine · run history"
        title="History"
      />
      <div className="glass-card divide-y divide-white/[0.05] overflow-hidden">
        {loadingCompanies ? (
          <div className="flex items-center justify-center gap-2 px-5 py-10 text-white/50 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading history...
          </div>
        ) : companies.length === 0 ? (
          <div className="px-5 py-10 text-center text-white/50 text-sm">
            No companies processed yet — start a workflow above to see results here.
          </div>
        ) : (
          companies.map((c) => (
            <div
              key={c.id}
              data-testid={`company-${c.id}`}
              role="button"
              tabIndex={0}
              onClick={() => openCompanyDetail(c)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openCompanyDetail(c); } }}
              className="grid grid-cols-7 items-center gap-4 px-5 py-4 cursor-pointer transition-colors hover:bg-white/[0.03]"
            >
              <div className="col-span-2 min-w-0">
                <div className="text-[14px] text-white truncate">{c.company_name || "—"}</div>
                <div className="mt-1 text-[11px] text-white/40 truncate">{c.company_url}</div>
              </div>
              <div className="col-span-1 min-w-0">
                {c.first_name ? (
                  <>
                    <div className="text-[13px] text-white/80 truncate">{c.first_name} {c.last_name}</div>
                    <div className="text-[11px] text-white/40 truncate">{c.email}</div>
                  </>
                ) : (
                  <span className="text-white/30 text-[13px]">No contact yet</span>
                )}
              </div>
              <CampaignMetric label="Match score" value={c.match_score ?? "—"} />
              <CampaignMetric label="Time taken" value={formatDuration(durationBetween(c.created_at, c.updated_at))} />
              <div className="col-span-2 flex items-center justify-end gap-3">
                <StatusStepper status={c.status} />
                <ChevronRight className="h-4 w-4 text-white/30" />
              </div>
            </div>
          ))
        )}
      </div>
      {companiesTotal > HISTORY_PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-between text-[11px] font-mono text-white/40">
          <span>{companiesOffset + 1}–{Math.min(companiesOffset + HISTORY_PAGE_SIZE, companiesTotal)} of {companiesTotal}</span>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              disabled={loadingCompanies || companiesOffset === 0}
              onClick={() => fetchCompanies(Math.max(0, companiesOffset - HISTORY_PAGE_SIZE))}
              className="rounded-full border-white/[0.1] bg-transparent text-white/70 hover:bg-white/[0.06] hover:text-white"
            >
              Previous
            </Button>
            <Button
              variant="outline" size="sm"
              disabled={loadingCompanies || companiesOffset + HISTORY_PAGE_SIZE >= companiesTotal}
              onClick={() => fetchCompanies(companiesOffset + HISTORY_PAGE_SIZE)}
              className="rounded-full border-white/[0.1] bg-transparent text-white/70 hover:bg-white/[0.06] hover:text-white"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Integrations strip */}
      {/* <SectionHeader className="mt-10" kicker="Wired-in" title="Integrations" />
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {[
          "Hunter.io", "Apollo", "Gmail", "Outlook",
          "OpenAI", "Claude", "Perplexity", "n8n",
        ].map((n) => (
          <div key={n} className="glass-card px-4 py-3 flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_6px_#10b981]" />
            <span className="text-[13px]">{n}</span>
          </div>
        ))}
      </div> */}

      <Dialog open={detailOpen} onOpenChange={(open) => { setDetailOpen(open); if (!open) setSelectedCompany(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto border-white/20 bg-[#0b0b0f] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">{selectedCompany?.company_name || "Company details"}</DialogTitle>
          </DialogHeader>
          {loadingDetail && !selectedCompany ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-white/50">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading...
            </div>
          ) : (
            <CompanyResultView company={selectedCompany} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CampaignMetric({ label, value }) {
  return (
    <div className="text-right">
      <div className="font-mono text-[10px] uppercase tracking-wider text-white/40">{label}</div>
      <div className="font-mono text-[15px] mt-0.5">{value}</div>
    </div>
  );
}

function WorkflowCanvas({ running }) {
  const w = 1160;
  const h = 320;
  const nodeMap = Object.fromEntries(WORKFLOW_NODES.map((n) => [n.id, n]));

  const bezier = (a, b) => {
    const x1 = a.x + 160, y1 = a.y + 30;
    const x2 = b.x, y2 = b.y + 30;
    const cx = (x1 + x2) / 2;
    return `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`;
  };

  return (
    <div className="canvas-grid relative overflow-x-auto" data-testid="workflow-canvas">
      <svg width={w} height={h} className="block">
        <defs>
          <linearGradient id="edgeGrad" x1="0" x2="1">
            <stop offset="0%" stopColor="#5E6AD2" />
            <stop offset="100%" stopColor="#00E5FF" />
          </linearGradient>
        </defs>
        {WORKFLOW_EDGES.map((e, i) => (
          <path
            key={i}
            d={bezier(nodeMap[e.from], nodeMap[e.to])}
            stroke="url(#edgeGrad)"
            strokeWidth="1.5"
            fill="none"
            opacity="0.7"
            className={running ? "flow-edge" : ""}
          />
        ))}
      </svg>
      {WORKFLOW_NODES.map((n) => (
        <motion.div
          key={n.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.3 }}
          className="absolute w-[160px] rounded-xl border border-white/[0.08] bg-[#0f0f13]/95 backdrop-blur-md p-3 shadow-[0_8px_30px_rgba(0,0,0,0.4)]"
          style={{ left: n.x, top: n.y }}
          data-testid={`workflow-node-${n.id}`}
        >
          <div className="flex items-center gap-2">
            <span
              className="h-2 w-2 rounded-full"
              style={{ background: n.color, boxShadow: `0 0 8px ${n.color}` }}
            />
            <span className="font-mono text-[10px] uppercase tracking-wider text-white/50">
              {n.kind}
            </span>
          </div>
          <div className="mt-1.5 font-display text-[13px] text-white">{n.label}</div>
          <div className="mt-0.5 font-mono text-[10.5px] text-white/50 truncate">{n.sub}</div>
        </motion.div>
      ))}
    </div>
  );
}
